import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Battery, Zap, ArrowLeft, Star, ShoppingCart, Shield, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : null;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/catalog/website/products/${slug}`);
        setProduct(res.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
      }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
        <p className="text-xl text-slate-400">Product not found</p>
        <Link to="/products" className="text-sm text-[#0061dd] hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to products
        </Link>
      </div>
    );
  }

  const p = product;
  const hasBuy = p.buy_from_price_ex_gst != null;
  const hasRent = p.rent_from_inr_per_kwh_month != null;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/products" className="flex items-center gap-2.5" data-testid="pdp-logo">
            <div className="w-8 h-8 rounded bg-[#0061dd] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight">Voltide</span>
          </Link>
          <Link to="/products" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> All Products
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <nav className="flex items-center gap-1.5 text-xs text-slate-500">
          <Link to="/products" className="hover:text-slate-300">Products</Link>
          <span>/</span>
          <span className="text-slate-400">{p.voltide_sku_code}</span>
        </nav>
      </div>

      {/* Product Detail */}
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* Left: Product info (3 cols) */}
          <div className="lg:col-span-3 space-y-8">
            {/* Title */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{p.voltide_sku_code}</span>
                <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">{p.sku_type}</Badge>
                {p.featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="pdp-title">
                {p.public_product_name}
              </h1>
            </div>

            {/* Specs grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="pdp-specs">
              <SpecCard icon={Zap} label="Rated Power" value={`${p.rated_power_kw} kW`} />
              <SpecCard icon={Battery} label="Energy Capacity" value={`${p.battery_energy_kwh} kWh`} />
              <SpecCard icon={Shield} label="Complexity" value={p.install_complexity} />
              {p.container_or_enclosure && (
                <SpecCard icon={CheckCircle} label="Form Factor" value={p.container_or_enclosure} />
              )}
            </div>

            {/* Features */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Key Features</h3>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />Grid-tied battery energy storage system</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{p.rated_power_kw} kW power with {p.battery_energy_kwh} kWh energy capacity</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />Integrated PCS and battery management system</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />Remote monitoring and diagnostics</li>
                {p.checkout_eligible && (
                  <li className="flex items-start gap-2"><ShoppingCart className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />Online checkout available</li>
                )}
              </ul>
            </div>

            {/* Disclaimer */}
            <div className="text-[11px] text-slate-600 leading-relaxed space-y-1">
              {p.disclaimer_text && <p>{p.disclaimer_text}</p>}
              <p>{p.rent_min_disclaimer}</p>
            </div>
          </div>

          {/* Right: Pricing card (2 cols) */}
          <div className="lg:col-span-2">
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 sticky top-8 space-y-6" data-testid="pdp-pricing-card">
              <h2 className="text-lg font-semibold">Pricing</h2>

              {/* Buy */}
              {hasBuy && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Buy (from)</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-2xl font-bold text-white" data-testid="pdp-buy-price">{fmt(p.buy_from_price_ex_gst)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">ex-GST</p>
                </div>
              )}

              {/* Rent */}
              {hasRent && (
                <div className="bg-emerald-950/30 border border-emerald-900/30 rounded-lg p-4">
                  <p className="text-[10px] text-emerald-400/70 uppercase tracking-wider mb-2">Rent (from)</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-2xl font-bold text-emerald-400" data-testid="pdp-rent-price">₹{Number(p.rent_from_inr_per_kwh_month).toFixed(2)}</span>
                    <span className="text-sm text-slate-500">/kWh/month</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">ex-GST</p>
                  {p.rent_assumptions_text && (
                    <p className="text-[10px] text-slate-600 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {p.rent_assumptions_text}
                    </p>
                  )}
                </div>
              )}

              {!hasBuy && !hasRent && (
                <div className="bg-slate-800/50 rounded-lg p-6 text-center">
                  <p className="text-sm text-slate-500">Pricing available on request</p>
                </div>
              )}

              {/* CTA */}
              <Button className="w-full bg-[#0061dd] hover:bg-[#0050bb] text-white h-11 text-sm font-medium" data-testid="pdp-enquiry-btn">
                Request a Quote
              </Button>
              <p className="text-[10px] text-slate-600 text-center leading-relaxed">
                {p.rent_min_disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-600">
          <span>&copy; {new Date().getFullYear()} Voltide Solutions Pvt. Ltd. All rights reserved.</span>
          <span>Prices starting from. Final quote may vary with SLA/site conditions.</span>
        </div>
      </footer>
    </div>
  );
}

function SpecCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3.5">
      <Icon className="w-4 h-4 text-slate-600 mb-2" />
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-white mt-0.5">{value}</p>
    </div>
  );
}
